import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/", label: "Home", end: true, id: "home" },
  { to: "/plans", label: "Plans", id: "plans" },
  { to: "/about", label: "About", id: "about" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/lookup", label: "Customer Lookup", id: "lookup" },
  { to: "/enquire", label: "Enquire", id: "enquire" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-[#0C1C2E]/80 backdrop-blur-xl border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          data-testid="brand-logo"
          onClick={() => setOpen(false)}
        >
          <span className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden p-0.5">
            <img src="/asa-logo.jpeg" alt="ASA logo" className="w-full h-full object-contain" />
          </span>
          <span className="leading-none">
            <span className="block font-heading font-extrabold text-xl tracking-tight text-white">ASA</span>
            <span className="hidden sm:block text-[10px] tracking-[0.18em] uppercase text-[#8CA0B4]">Home Appliance Services</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-300 hover:text-[#F26A21] ${
                  isActive ? "text-[#F26A21]" : "text-[#AEBECD]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/get-id")}
            data-testid="nav-get-id"
            className="hidden md:inline-flex items-center gap-2 bg-[#F26A21] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#DE5C15] transition-colors duration-300 group"
          >
            Get Customer ID
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            className="md:hidden overflow-hidden bg-[#0C1C2E] border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.id}`}
                  className={({ isActive }) =>
                    `font-heading text-2xl font-semibold ${
                      isActive ? "text-[#F26A21]" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/get-id");
                }}
                data-testid="mobile-nav-get-id"
                className="mt-2 bg-[#F26A21] text-white px-5 py-4 rounded-full font-semibold text-left"
              >
                Get Customer ID →
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
